import { css } from '@styled-system/css';
import { TriangleAlert } from 'lucide-react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/shared/components/ui/Button';
import { Modal } from '@/shared/components/ui/Modal';

import { useCredits, type CreditKind } from './useCredits';

// ============================================================================
// Popup « Limite mensuelle atteinte » — affiché quand une création de
// recherche est bloquée par le quota mensuel. CTA vers la page abonnement
// (et mention de l'essai gratuit : quotas réduits → passer au payant).
// ============================================================================

const body = css({ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4', textAlign: 'center' });

const iconWrap = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '48px',
  height: '48px',
  borderRadius: 'full',
  bg: 'warning.soft',
  color: 'warning.default',
});

const title = css({ fontSize: 'body', fontWeight: 'semibold', color: 'text.primary' });

const hint = css({ fontSize: 'bodySm', color: 'text.secondary', lineHeight: 1.6, maxWidth: '360px' });

interface QuotaReachedModalProps {
  isOpen: boolean;
  kind: CreditKind;
  onClose: () => void;
}

export const QuotaReachedModal: FC<QuotaReachedModalProps> = ({ isOpen, kind, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const usage = useCredits();
  const max = kind === 'vendor' ? usage.limits.vendor : usage.limits.product;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('credits.quotaModalTitle')}
      width="420px"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('credits.quotaLater')}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              onClose();
              navigate('/subscription');
            }}
          >
            {usage.trialing ? t('credits.quotaCtaTrial') : t('credits.ctaUpgrade')}
          </Button>
        </>
      }
    >
      <div className={body}>
        <span className={iconWrap}>
          <TriangleAlert size={22} strokeWidth={1.75} />
        </span>
        <span className={title}>
          {kind === 'vendor' ? t('credits.quotaUsedVendor', { max }) : t('credits.quotaUsedProduct', { max })}
        </span>
        <span className={hint}>{usage.trialing ? t('credits.quotaHintTrial') : t('credits.quotaHint')}</span>
      </div>
    </Modal>
  );
};
